/**
 * <mobile-menu> — slide-in mobile navigation drawer with multi-level navigation.
 *
 * Extends DrawerComponent for open/close/toggle, overlay, ESC, body-scroll-lock
 * and focus-trap (opened from the header hamburger via `data-toggle-id`).
 *
 * On top of that it drives the in-drawer level navigation, scoped to this
 * element. The CSS contract (`assets/section-sidebar_menu.css`) is:
 *   - `.mobile-menu__link-header[aria-hidden]`  — top-level tab bar
 *   - `.mobile-menu__nav-header[aria-hidden]`    — back button + breadcrumb (level 2+)
 *   - `.mobile-menu__link-content[aria-hidden]`  — one panel per top-level tab
 *   - `.mobile-menu__link-content[data-active-level="2"|"3"]` — which level shows
 *   - `.mobile-menu__level--second|--third[aria-hidden]` — sub-panels
 *
 * Triggers (delegated):
 *   data-action="toggle-mobile-content" (data-id)             — open/close a tab's links
 *   data-action="open-sublevel"   (aria-controls, data-title) — level 1 → 2
 *   data-action="open-thirdlevel" (aria-controls, data-title) — level 2 → 3
 *   data-action="nav-back"                                    — up one level
 *
 * Loaded ONLY from `sections/main-mobile_menu.liquid` via
 * `type="module" fetchpriority="low"`.
 *
 * Dependencies:
 *   - @theme/drawer-component
 */

import { DrawerComponent } from '@theme/drawer-component';

class MobileMenu extends DrawerComponent {
  /** @type {((event: MouseEvent) => void) | null} */
  #onNavClick = null;

  /** @type {((event: KeyboardEvent) => void) | null} */
  #onNavKeydown = null;

  connectedCallback() {
    super.connectedCallback();
    this.#bindNav();
  }

  disconnectedCallback() {
    this.#unbindNav();
    super.disconnectedCallback();
  }

  /** Open the first tab so the menu never appears empty when it opens. */
  onOpened() {
    this.#openFirstTab();
    this.#playCollectionVideos();
  }

  /** Reset back to the top-level tab view whenever the drawer closes. */
  onClosed() {
    this.#resetToTabs();
    this.#pauseCollectionVideos();
  }

  // Videos ship with preload="none" and no autoplay (see
  // mobile-menu_collections.liquid) so a closed menu never downloads them;
  // play() triggers the load on first open. When playback is unavailable
  // (reduced motion, iOS Low Power Mode) load just the first frame so the
  // tile shows content instead of an empty box.
  #playCollectionVideos() {
    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    this.querySelectorAll('.mobile-menu__collection-video').forEach((video) => {
      if (reducedMotion) {
        this.#loadVideoPosterFrame(video);
        return;
      }
      const playing = video.play();
      playing?.catch?.(() => this.#loadVideoPosterFrame(video));
    });
  }

  #loadVideoPosterFrame(video) {
    if (video.readyState > 0) return;
    video.preload = 'metadata';
    video.load();
  }

  #pauseCollectionVideos() {
    this.querySelectorAll('.mobile-menu__collection-video').forEach((video) => {
      video.pause();
    });
  }

  #openFirstTab() {
    // Nothing to do if a tab is already open (e.g. reopened mid-session).
    if (this.querySelector('.mobile-menu__link-content[aria-hidden="false"]')) return;
    const firstTab = this.querySelector(
      '.mobile-menu__link-header [data-action="toggle-mobile-content"]'
    );
    if (firstTab) this.#toggleTab(firstTab);
  }

  #bindNav() {
    this.#onNavClick = (event) => {
      const trigger = event.target.closest('[data-action]');
      if (!trigger || !this.contains(trigger)) return;

      switch (trigger.dataset.action) {
        case 'toggle-mobile-content':
          this.#toggleTab(trigger);
          break;
        case 'open-sublevel':
          this.#openLevel(trigger, 2);
          break;
        case 'open-thirdlevel':
          this.#openLevel(trigger, 3);
          break;
        case 'nav-back':
          this.#navBack();
          break;
        case 'open-drawer':
          this.#tagLocalizationDrawer(trigger);
          break;
        default:
          // Other actions (e.g. toggle-dropdown) are handled elsewhere.
          break;
      }
    };
    this.addEventListener('click', this.#onNavClick);

    // The swiper tab variant renders tabs as `<div tabindex="0">`, not <button>,
    // so add keyboard activation for those.
    this.#onNavKeydown = (event) => {
      if (event.key !== 'Enter' && event.key !== ' ') return;
      const trigger = event.target.closest('[data-action="toggle-mobile-content"]');
      if (!trigger || trigger.tagName === 'BUTTON' || !this.contains(trigger)) return;
      event.preventDefault();
      this.#toggleTab(trigger);
    };
    this.addEventListener('keydown', this.#onNavKeydown);
  }

  #unbindNav() {
    if (this.#onNavClick) {
      this.removeEventListener('click', this.#onNavClick);
      this.#onNavClick = null;
    }
    if (this.#onNavKeydown) {
      this.removeEventListener('keydown', this.#onNavKeydown);
      this.#onNavKeydown = null;
    }
  }

  /** Open a top-level tab's links (or close it if it is already open). */
  #toggleTab(button) {
    const id = button.dataset.id;
    if (!id) return;
    // Match on dataset rather than a `[data-id="…"]` selector: Shopify block ids can
    // start with a digit / contain characters that break attribute-value selectors.
    const content = Array.from(this.querySelectorAll('.mobile-menu__link-content')).find(
      (c) => c.dataset.id === id
    );
    if (!content) return;

    const alreadyOpen = content.getAttribute('aria-hidden') === 'false';

    // Reselecting a tab always returns to the top level first.
    this.#resetToTabs();
    if (alreadyOpen) return;

    content.setAttribute('aria-hidden', 'false');
    button.setAttribute('aria-expanded', 'true');
  }

  /**
   * Drill into a sub-panel. `level` is 2 (sublevel) or 3 (thirdlevel).
   * @param {HTMLElement} button
   * @param {2 | 3} level
   */
  #openLevel(button, level) {
    const content = button.closest('.mobile-menu__link-content');
    if (!content) return;

    const panelId = button.getAttribute('aria-controls');
    // getElementById takes the raw id (no selector escaping) and panel ids are unique;
    // guard that the panel really belongs to this tab's content.
    const panel = panelId ? document.getElementById(panelId) : null;
    if (!panel || !content.contains(panel)) return;

    const levelClass = level === 3 ? 'mobile-menu__level--third' : 'mobile-menu__level--second';
    content.querySelectorAll(`.${levelClass}`).forEach((p) => {
      p.setAttribute('aria-hidden', p === panel ? 'false' : 'true');
    });
    button.setAttribute('aria-expanded', 'true');
    content.setAttribute('data-active-level', String(level));

    this.#showNavHeader();
    this.#setBreadcrumb(level, content);
  }

  /** Step up one level (3 → 2 → tabs). */
  #navBack() {
    const content = this.querySelector('.mobile-menu__link-content[aria-hidden="false"]');
    if (!content) return;

    const level = parseInt(content.getAttribute('data-active-level'), 10) || 1;

    if (level >= 3) {
      content
        .querySelector('.mobile-menu__level--third[aria-hidden="false"]')
        ?.setAttribute('aria-hidden', 'true');
      content
        .querySelectorAll('[data-action="open-thirdlevel"]')
        .forEach((b) => b.setAttribute('aria-expanded', 'false'));
      content.setAttribute('data-active-level', '2');
      this.#setBreadcrumb(2, content);
    } else if (level === 2) {
      content
        .querySelector('.mobile-menu__level--second[aria-hidden="false"]')
        ?.setAttribute('aria-hidden', 'true');
      content
        .querySelectorAll('[data-action="open-sublevel"]')
        .forEach((b) => b.setAttribute('aria-expanded', 'false'));
      content.removeAttribute('data-active-level');
      this.#showTabBar();
      this.#clearBreadcrumb();
    }
  }

  #tagLocalizationDrawer(trigger) {
    if (trigger.dataset.toggleId !== 'footer-localization-drawer') return;
    const drawer = document.getElementById('footer-localization-drawer');
    if (!drawer) return;

    drawer.classList.add('Drawer--from-mobile-menu');

    const onOpened = (event) => {
      if (event.target !== drawer) return;
      document.removeEventListener('drawer:opened', onOpened);
      requestAnimationFrame(() => {
        requestAnimationFrame(() => drawer.classList.add('Drawer--menu-fade'));
      });
    };
    document.addEventListener('drawer:opened', onOpened);

    const onClosed = (event) => {
      if (event.target !== drawer) return;
      document.removeEventListener('drawer:closed', onClosed);

      const finalize = () => {
        drawer.removeEventListener('transitionend', onFadeEnd);
        if (!drawer.classList.contains('is-open')) {
          drawer.classList.remove('Drawer--from-mobile-menu', 'Drawer--menu-fade');
        }
      };
      const onFadeEnd = (e) => {
        if (e.target === drawer && e.propertyName === 'opacity') finalize();
      };
      drawer.addEventListener('transitionend', onFadeEnd);
      setTimeout(finalize, 450);
    };
    document.addEventListener('drawer:closed', onClosed);
  }

  /** Collapse everything back to the top-level tab bar. */
  #resetToTabs() {
    this.querySelectorAll('.mobile-menu__link-content').forEach((content) => {
      content.setAttribute('aria-hidden', 'true');
      content.removeAttribute('data-active-level');
      content
        .querySelectorAll('.mobile-menu__level--second, .mobile-menu__level--third')
        .forEach((lvl) => lvl.setAttribute('aria-hidden', 'true'));
      content
        .querySelectorAll('[data-action="open-sublevel"], [data-action="open-thirdlevel"]')
        .forEach((b) => b.setAttribute('aria-expanded', 'false'));
    });
    this.querySelectorAll('.mobile-menu__link-button[aria-expanded]').forEach((b) =>
      b.setAttribute('aria-expanded', 'false')
    );
    this.#showTabBar();
    this.#clearBreadcrumb();
  }

  #showTabBar() {
    this.querySelector('.mobile-menu__link-header')?.setAttribute('aria-hidden', 'false');
    this.querySelector('.mobile-menu__nav-header')?.setAttribute('aria-hidden', 'true');
  }

  #showNavHeader() {
    this.querySelector('.mobile-menu__link-header')?.setAttribute('aria-hidden', 'true');
    this.querySelector('.mobile-menu__nav-header')?.setAttribute('aria-hidden', 'false');
  }

  /**
   * Breadcrumb shows the current level's title in `[data-nav-child]`.
   * @param {2 | 3} level
   * @param {HTMLElement} content
   */
  #setBreadcrumb(level, content) {
    let child = '';
    if (level === 2) {
      child =
        content.querySelector('.mobile-menu__level--second[aria-hidden="false"]')?.dataset.sublevelTitle ||
        '';
    } else if (level === 3) {
      child =
        content.querySelector('.mobile-menu__level--third[aria-hidden="false"]')?.dataset.thirdlevelTitle ||
        '';
    }
    this.#renderBreadcrumb(child);
  }

  #clearBreadcrumb() {
    this.#renderBreadcrumb('');
  }

  /** @param {string} child */
  #renderBreadcrumb(child) {
    const childEl = this.querySelector('[data-nav-child]');
    if (childEl) childEl.textContent = child;
  }
}

if (!customElements.get('mobile-menu')) {
  customElements.define('mobile-menu', MobileMenu);
}
